import React from 'react';
import { Helmet } from 'react-helmet-async';
import {
  generateMetaTags,
  generateBlogPostSchema,
  generateBlogPostMeta,
  generateFAQSchema,
  generateOrganizationSchema,
  generateWebsiteSchema,
  extractFAQs,
  optimizeTitle,
  optimizeDescription,
  validateAndFixUrl
} from '../utils/seoHelper';

const SEOHead = ({
  title,
  description,
  image,
  url,
  canonicalUrl,
  type = 'website',
  publishedTime,
  modifiedTime,
  author,
  tags = [],
  category,
  content,
  faqContent,
  blog,
  htmlMetaTags // Pre-generated meta tags from blog JSON
}) => {
  // 🎯 PRIORITY: Use pre-generated html_meta_tags if available
  if (htmlMetaTags && typeof htmlMetaTags === 'string' && htmlMetaTags.trim() !== '') {
    // Parse the HTML string and extract meta tags
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlMetaTags, 'text/html');

    // Extract title
    const titleElement = doc.querySelector('title');
    const pageTitle = titleElement ? titleElement.textContent : title;

    // Extract all meta tags
    const metaTags = Array.from(doc.querySelectorAll('meta')).map(meta => {
      const attrs = {};
      Array.from(meta.attributes).forEach(attr => {
        attrs[attr.name] = attr.value;
      });
      return attrs;
    });

    // Extract link tags (excluding canonical, we will render it explicitly)
    const linkTags = Array.from(doc.querySelectorAll('link'))
      .map(link => {
        const attrs = {};
        Array.from(link.attributes).forEach(attr => {
          attrs[attr.name] = attr.value;
        });
        return attrs;
      })
      .filter(link => link.rel !== 'canonical');

    // Extract JSON-LD scripts
    const scripts = Array.from(doc.querySelectorAll('script[type="application/ld+json"]')).map(script => {
      return script.textContent;
    });

    // Determine correct canonical URL
    const parsedCanonical = doc.querySelector('link[rel="canonical"]')?.getAttribute('href');
    const finalCanonical = canonicalUrl || url || parsedCanonical || `https://dailytechtrend.com/`;

    return (
      <Helmet prioritizeSeoTags>
        <title>{pageTitle}</title>
        <link rel="canonical" href={finalCanonical} />
        {linkTags.map((link, index) => (
          <link key={`link-${index}`} {...link} />
        ))}
        {metaTags.map((meta, index) => (
          <meta key={`meta-${index}`} {...meta} />
        ))}
        {scripts.map((scriptContent, index) => (
          <script key={`schema-${index}`} type="application/ld+json">
            {scriptContent}
          </script>
        ))}
      </Helmet>
    );
  }

  // Fallback to legacy SEO generation for pages without html_meta_tags
  let metaTags;
  if (blog && type === 'article') {
    // Extract metadata from blog JSON (check SEO object first)
    const blogMetadata = blog.SEO || blog.metadata || {};

    // Use canonical URL from metadata or passed parameter
    const finalCanonical = canonicalUrl || url || blogMetadata.canonical_url || blog.url;

    // 🎯 NEW: Check if og/twitter data has placeholders or empty strings
    const ogData = blogMetadata.og || {};
    const isOgImageValid = ogData.image && ogData.image.trim() !== '' &&
      !ogData.image.includes('[') && !ogData.image.includes('Placeholder');
    const isOgUrlValid = ogData.url && ogData.url.trim() !== '' &&
      !ogData.url.includes('[') && !ogData.url.includes('Placeholder');

    const finalOgImage = isOgImageValid ? ogData.image : (image || blogMetadata.image);
    const finalOgTitle = ogData.title || title || blogMetadata.title || blogMetadata.meta_title;
    const finalOgDescription = ogData.description || description || blogMetadata.meta_description;
    const finalOgUrl = isOgUrlValid ? ogData.url : finalCanonical;

    // Use Twitter card data from metadata
    const twitterData = blogMetadata.twitter || {};
    const isTwitterImageValid = twitterData.image && twitterData.image.trim() !== '' &&
      !twitterData.image.includes('[') && !twitterData.image.includes('Placeholder');
    const finalTwitterImage = isTwitterImageValid ? twitterData.image : (image || blogMetadata.image);

    // Generate comprehensive meta tags using all available metadata
    metaTags = generateBlogPostMeta({
      ...blog,
      url: finalCanonical,
      metadata: {
        ...blogMetadata,
        title: finalOgTitle,
        meta_description: finalOgDescription,
        image: finalOgImage,
        og: {
          ...ogData,
          image: finalOgImage,
          title: finalOgTitle,
          description: finalOgDescription,
          url: finalOgUrl
        },
        twitter: {
          ...twitterData,
          image: finalTwitterImage,
          title: twitterData.title || finalOgTitle,
          description: twitterData.description || finalOgDescription
        }
      }
    });
  } else {
    // Optimize individual components for non-article pages
    const optimizedTitle = optimizeTitle(title);
    const optimizedDescription = optimizeDescription(description || content);
    const cleanUrl = validateAndFixUrl(url);

    metaTags = generateMetaTags({
      title: optimizedTitle,
      description: optimizedDescription,
      image,
      url: cleanUrl,
      type,
      publishedTime: publishedTime ? new Date(publishedTime) : undefined,
      modifiedTime: modifiedTime ? new Date(modifiedTime) : undefined,
      author,
      section: category,
      tags
    });
  }

  // Generate structured data
  let schemas = [];

  // 🎯 NEW: Check if blog has pre-generated schema_jsonld string
  const blogMetadata = blog?.SEO || blog?.metadata || {};
  const preGeneratedSchema = blogMetadata.schema_jsonld;

  // Use pre-generated schema if available, otherwise generate from scratch
  if (type === 'article' && preGeneratedSchema && typeof preGeneratedSchema === 'string' && preGeneratedSchema.trim() !== '') {
    try {
      // Parse and validate the pre-generated schema
      const parsedSchema = JSON.parse(preGeneratedSchema);
      if (parsedSchema['@context'] && parsedSchema['@type']) {
        schemas.push({
          type: 'application/ld+json',
          innerHTML: preGeneratedSchema
        });
      } else {
        // Invalid schema - fall back to generation
        console.warn('Pre-generated schema_jsonld is invalid, generating new schema');
        throw new Error('Invalid schema structure');
      }
    } catch (error) {
      console.warn('Failed to parse pre-generated schema_jsonld:', error);
      // Fall back to generating schema
      if (title) {
        const blogPost = blog || {
          id: url?.split('/').pop() || 'unknown',
          title,
          content: content || description,
          excerpt: description,
          image,
          author,
          date: publishedTime,
          category: category || (tags.length > 0 ? tags[0] : 'AI Technology'),
          tags,
          url
        };
        schemas.push(generateBlogPostSchema(blogPost));
      }
    }
  } else {
    // No pre-generated schema available - generate from scratch
    // Website schema for homepage ONLY (not for blog posts or other pages)
    if (type === 'website' && url && (url === '/' || url === 'https://dailytechtrend.com/' || url.includes('dailytechtrend.com/?') || (!url.includes('/about') && !url.includes('/contact')))) {
      schemas.push(generateWebsiteSchema());
    }

    // Blog post schema for articles ONLY
    if (type === 'article' && title) {
      const blogPost = blog || {
        id: url?.split('/').pop() || 'unknown',
        title,
        content: content || description,
        excerpt: description,
        image,
        author,
        date: publishedTime,
        category: category || (tags.length > 0 ? tags[0] : 'AI Technology'),
        tags,
        url
      };

      schemas.push(generateBlogPostSchema(blogPost));
    }
  }

  // FAQ schema if FAQ content is provided
  if (faqContent) {
    const faqs = extractFAQs(faqContent);
    if (faqs.length > 0) {
      schemas.push(generateFAQSchema(faqs));
    }
  }

  // Organization schema for ALL pages (helps with site-wide branding)
  schemas.push(generateOrganizationSchema());

  // 🎯 CRITICAL: Ensure canonical URL is ALWAYS set correctly for ALL page types
  if (canonicalUrl && metaTags?.link) {
    // Explicit canonicalUrl provided - use it (highest priority)
    const finalCanonicalUrl = canonicalUrl.startsWith('http') ? canonicalUrl : `https://dailytechtrend.com${canonicalUrl}`;

    // Remove ALL existing canonical links
    metaTags.link = metaTags.link.filter(l => l.rel !== 'canonical');
    // Add the correct canonical link at the beginning (most important)
    metaTags.link.unshift({ rel: 'canonical', href: finalCanonicalUrl });
  } else if (url && metaTags?.link) {
    // URL provided but no explicit canonical - construct it
    const finalCanonicalUrl = url.startsWith('http') ? url :
      url === '/' ? 'https://dailytechtrend.com/' :
        url.startsWith('/') ? `https://dailytechtrend.com${url}` :
          `https://dailytechtrend.com/${url}`;

    // Remove ALL existing canonical links
    metaTags.link = metaTags.link.filter(l => l.rel !== 'canonical');
    // Add the correct canonical link
    metaTags.link.unshift({ rel: 'canonical', href: finalCanonicalUrl });
  }

  return (
    <Helmet prioritizeSeoTags>
      {/* Page title - MUST come first */}
      <title>{metaTags.title}</title>

      {/* Canonical URL - CRITICAL: Must be set early */}
      {metaTags.link
        .filter(link => link.rel === 'canonical')
        .map((link, index) => (
          <link key={`canonical-${index}`} {...link} />
        ))}

      {/* Meta tags */}
      {metaTags.meta.map((tag, index) => (
        <meta key={`meta-${index}`} {...tag} />
      ))}

      {/* Other link tags (excluding canonical which was already added) */}
      {metaTags.link
        .filter(link => link.rel !== 'canonical')
        .map((link, index) => (
          <link key={`link-${index}`} {...link} />
        ))}

      {/* Structured data schemas */}
      {schemas.map((schema, index) => (
        <script key={`schema-${index}`} type={schema.type}>
          {schema.innerHTML}
        </script>
      ))}
    </Helmet>
  );
};

export default SEOHead;